import React, { useState } from 'react';
import { Alert, Button, Content, Form, Modal, ModalBody, ModalFooter, ModalHeader } from '@patternfly/react-core';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAdminGpuActions } from '../../../hooks/useAdminVerdaServers';
import type { VerdaGpuServer } from '../../../types/models/Verda';
import { destroyOutcomeMessage } from './verdaGpuServers';
import { TypedConfirmField, typedMatches } from './VerdaGpuParts';

interface VerdaDestroyServerModalProps {
  server: VerdaGpuServer;
  onClose: () => void;
  /** Something may have changed: the detail refetches. */
  onChanged: () => void;
}

/**
 * Staff force delete (`POST /admin/verda/servers/{id}/destroy`): a real, tag-checked delete at Verda,
 * irreversible. Typed-hostname confirmation. For a `lost` row it only deletes the leftover instance —
 * the row stays lost. Mounted only while open.
 */
function VerdaDestroyServerModal({ server, onClose, onChanged }: VerdaDestroyServerModalProps) {
  const { destroy, busy } = useAdminGpuActions();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isLost = server.status === 'lost';
  const client = server.client.name ?? `client #${server.client.id}`;

  const handleDestroy = async () => {
    setError(null);
    const outcome = await destroy(server.id);
    if (outcome.status === 'error') {
      setError(outcome.message);
      addNotification({
        title: `Could not destroy ${server.hostname}`,
        srTitle: 'Error alert',
        description: outcome.message,
        variant: 'danger',
      });
      setDrawerOpen(true);
      return;
    }
    const pending = outcome.data.outcome === 'pending';
    addNotification({
      title: pending ? `${server.hostname}: provider unreachable` : `${server.hostname}: destroy sent`,
      srTitle: pending ? 'Warning alert' : 'Success alert',
      description: destroyOutcomeMessage(outcome.data.outcome, isLost),
      variant: pending ? 'warning' : 'success',
    });
    setDrawerOpen(true);
    onChanged();
    onClose();
  };

  return (
    <Modal variant="small" isOpen onClose={busy ? undefined : onClose} aria-labelledby="verda-destroy-title">
      <ModalHeader
        title="Destroy server"
        labelId="verda-destroy-title"
        titleIconVariant="danger"
        description={server.hostname}
      />
      <ModalBody>
        {error !== null && (
          <Alert variant="danger" isInline title="Could not destroy the server" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        <Content component="p">
          {isLost ? (
            <>
              <strong>{server.hostname}</strong> is lost: this deletes its leftover instance at Verda, if it still
              exists. The row stays lost whatever the outcome.
            </>
          ) : (
            <>
              This deletes <strong>{server.hostname}</strong>, {client}&apos;s machine, at Verda. It cannot be undone.
            </>
          )}
        </Content>
        <Content component="ul">
          <Content component="li">The disk goes to Verda&apos;s 96 h trash.</Content>
          <Content component="li">No email is sent to the customer.</Content>
          <Content component="li">The current hour is not refunded (refund it separately if needed).</Content>
          <Content component="li">It acts even while the destroy switch (GPU_DESTROY_ENABLED) is off.</Content>
        </Content>
        <Form onSubmit={(event) => event.preventDefault()}>
          <TypedConfirmField
            id="verda-destroy-confirm"
            expected={server.hostname}
            value={typed}
            onChange={setTyped}
            hint="Destroying is irreversible, so the hostname is typed out in full."
          />
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="danger"
          onClick={() => void handleDestroy()}
          isLoading={busy}
          isDisabled={busy || !typedMatches(typed, server.hostname)}
        >
          Destroy server
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={busy}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default VerdaDestroyServerModal;
